import type {
  IDynamicFields,
  IExternalField,
  IFieldGroup,
  IResource,
  ITicket,
  IUser
} from '@rready/sdk';
import { updateTicketFieldDefinition } from './tools/updateTicketField';
import { suggestTicketImprovementDefinition } from './tools/suggestTicketImprovement';
import { getTicketUpdateRequest, ITicketModel } from '@rready/composables';

export function ticketCleanup(ticket: ITicket) {
  const clean: Partial<ITicket> = { ...ticket.meta, title: ticket.title };
  // To prevent the AI from referring to new tickets as `{{ ticket.title }}`
  clean.title = clean.title ?? '';
  return clean;
}

const AvailableTypes: ReadonlyArray<string> = [
  'text',
  'textarea',
  'textareaMarkdown'
];

export function dynamicFieldSummary(
  df: IDynamicFields,
  $res: <T>(resource: IResource<T>) => T
) {
  return df.externalFields
    .flatMap((it) => it.fields)
    .filter((it) => AvailableTypes.includes(it.type))
    .map((it) => dynamicField(it, $res));
}

export function dynamicField(
  df: IExternalField,
  $res: <T>(resource: IResource<T>) => T
) {
  return {
    key: df.ref,
    type: df.type,
    label: $res(df.options?.resources ?? {}).label
  };
}

export function createTicketEditContext(
  ticketSource: ITicket,
  ticketModel: ITicketModel,
  dynamicFields: IDynamicFields,
  user: IUser,
  $res: <T>(resource: IResource<T>) => T
) {
  const ticket = getTicketUpdateRequest(
    ticketModel.title,
    ticketModel.dynamicState
  ) as ITicket;

  const titleField = {
    groupId: 'title',
    groupLabel: 'title',
    fields: [
      {
        ref: 'title',
        type: 'text',
        required: true,
        options: {
          resources: {
            en: { label: 'Title' },
            fr: { label: 'Titre' },
            it: { label: 'Titolo' },
            de: { label: 'Titel' },
            az: { label: 'Başlıq' }
          }
        }
      }
    ]
  } as IFieldGroup;

  return {
    ticketSource,
    user,
    ticket,
    ticketJson: JSON.stringify(ticketCleanup(ticket)),
    fieldsJson: JSON.stringify(
      dynamicFieldSummary(
        {
          ...dynamicFields,
          externalFields: [titleField, ...dynamicFields.externalFields]
        },
        $res
      )
    )
  };
}

export function createSimilarTicketImprovementContext(
  userTicket: ITicket,
  similarTickets: ITicket[],
  user: IUser
) {
  return {
    user,
    ticketJson: JSON.stringify(ticketCleanup(userTicket)),
    similarTicketsJson: JSON.stringify(similarTickets.map(ticketCleanup))
  };
}

export const aiAgentTools = {
  updateTicketFieldDefinition,
  suggestTicketImprovementDefinition
};
